import { PropertyType } from '@app/models/enums'
import { IProperty, Property } from '@app/models/Property'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface IPropertiesListState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<IProperty>
  readonly orderBy: string
  readonly propertyTypes: List<PropertyType>
  readonly sortOrder: string
  readonly totalItems: number
}

const PropertiesListStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<Property>(),
  propertyTypes: List<PropertyType>(),
  sortOrder: 'DateCreated',
  orderBy: 'Descending',
  totalItems: 0,
})

export class PropertiesListState
  extends PropertiesListStateRecord
  implements IPropertiesListState
{
  public static fromJS(initialState?: Partial<IPropertiesListState>) {
    if (!initialState) {
      return new PropertiesListState()
    }

    const { items, propertyTypes, ...rest } = initialState
    return new PropertiesListState({
      items: items ? List(items.map((i) => Property.fromJS(i))) : undefined,
      propertyTypes: propertyTypes
        ? List(propertyTypes.map((propertyType) => propertyType))
        : List<PropertyType>(),
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<Property>
  public readonly propertyTypes: List<PropertyType>
  public readonly sortOrder: string
  public readonly orderBy: string
  public readonly totalItems: number

  constructor(props?: Partial<PropertiesListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum PropertiesListActionTypes {
  LoadProperties = 'PROPERTIES_LIST::LOAD_PROPERTIES',
  LoadPropertiesFailure = 'PROPERTIES_LIST::LOAD_PROPERTIES_FAILURE',
  LoadPropertiesRequest = 'PROPERTIES_LIST::LOAD_PROPERTIES_REQUEST',
  LoadPropertiesSuccess = 'PROPERTIES_LIST::LOAD_PROPERTIES_SUCCESS',
  SetFilterText = 'PROPERTIES_LIST::SET_FILTER_TEXT',
  SetOrderBy = 'PROPERTIES_LIST::SET_ORDER_BY',
  SetPropertyTypes = 'PROPERTIES_LIST::SET_PROPERTY_TYPES',
  SetSortOrder = 'PROPERTIES_LIST::SET_SORT_ORDER',
}

interface ILoadPropertiesAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: PropertiesListActionTypes.LoadProperties
}

interface ILoadPropertiesFailureAction {
  error: string
  type: PropertiesListActionTypes.LoadPropertiesFailure
}

interface ILoadPropertiesRequestAction {
  type: PropertiesListActionTypes.LoadPropertiesRequest
}

interface ILoadPropertiesSuccessAction {
  append: boolean
  items: Property[]
  totalItems: number
  type: PropertiesListActionTypes.LoadPropertiesSuccess
}

interface ISetFilterTextAction {
  filter: string
  type: PropertiesListActionTypes.SetFilterText
}

interface ISetOrderByAction {
  orderBy: string
  type: PropertiesListActionTypes.SetOrderBy
}

interface ISetPropertyTypeAction {
  propertyTypes: List<PropertyType>
  type: PropertiesListActionTypes.SetPropertyTypes
}

interface ISetSortOrderAction {
  sortOrder: string
  type: PropertiesListActionTypes.SetSortOrder
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadPropertiesFailureAction
  | ILoadPropertiesRequestAction
  | ILoadPropertiesSuccessAction
  | ISetFilterTextAction
  | ISetOrderByAction
  | ISetPropertyTypeAction
  | ISetSortOrderAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadProperties(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadPropertiesAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: PropertiesListActionTypes.LoadProperties,
  }
}

function loadPropertiesFailure(error: string): ILoadPropertiesFailureAction {
  return {
    error,
    type: PropertiesListActionTypes.LoadPropertiesFailure,
  }
}

function loadPropertiesRequest(): ILoadPropertiesRequestAction {
  return {
    type: PropertiesListActionTypes.LoadPropertiesRequest,
  }
}

function loadPropertiesSuccess(
  items: Property[],
  append: boolean,
  totalItems: number,
): ILoadPropertiesSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: PropertiesListActionTypes.LoadPropertiesSuccess,
  }
}

function setPropertyTypes(
  propertyTypes: List<PropertyType>,
): ISetPropertyTypeAction {
  return {
    propertyTypes,
    type: PropertiesListActionTypes.SetPropertyTypes,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: PropertiesListActionTypes.SetFilterText,
  }
}

function setOrderBy(orderBy: string): ISetOrderByAction {
  return {
    orderBy,
    type: PropertiesListActionTypes.SetOrderBy,
  }
}

function setSortOrder(sortOrder: string) {
  return {
    sortOrder,
    type: PropertiesListActionTypes.SetSortOrder,
  }
}

export const actionCreators = {
  loadProperties,
  setFilterText,
  setOrderBy,
  setPropertyTypes,
  setSortOrder,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<PropertiesListState> = (
  state: PropertiesListState = new PropertiesListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case PropertiesListActionTypes.LoadPropertiesFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as PropertiesListState

    case PropertiesListActionTypes.LoadPropertiesRequest:
      return state.set('isLoading', true) as PropertiesListState

    case PropertiesListActionTypes.LoadPropertiesSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<Property>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as PropertiesListState

    case PropertiesListActionTypes.SetPropertyTypes:
      return state.set(
        'propertyTypes',
        action.propertyTypes,
      ) as PropertiesListState

    case PropertiesListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as PropertiesListState

    case PropertiesListActionTypes.SetOrderBy:
      return state.set('orderBy', action.orderBy) as PropertiesListState

    case PropertiesListActionTypes.SetSortOrder:
      return state.set('sortOrder', action.sortOrder) as PropertiesListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadPropertiesSaga(action: ILoadPropertiesAction) {
  try {
    yield put(loadPropertiesRequest())

    const currentState: PropertiesListState = yield select(
      (state: ApplicationState) => state.propertiesList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (currentState.propertyTypes.count() > 0) {
      query.push(`propertyTypes=${currentState.propertyTypes.join('-')}`)
    }

    if (currentState.sortOrder !== 'DateCreated') {
      query.push(`sort=${currentState.sortOrder}`)
    }

    if (currentState.orderBy) {
      query.push(`orderBy=${currentState.orderBy}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const url = `/api/Properties${
      query.length > 0 ? `?${query.join('&')}` : ''
    }`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: Property[] = result.results.map((c: any) =>
        Property.fromJS(c),
      )
      yield put(
        loadPropertiesSuccess(contacts, action.append, result.paging.total),
      )
    } else {
      yield put(loadPropertiesFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadPropertiesFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadProperties() {
  yield takeLatest(PropertiesListActionTypes.LoadProperties, loadPropertiesSaga)
}

export function* propertiesListRootSaga() {
  yield fork(watchLoadProperties)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState =
  () =>
  (state: ApplicationState): PropertiesListState =>
    state.propertiesList

export const selectPropertyTypes = () =>
  createSelector(selectModuleState(), (mState) => mState.propertyTypes)

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectFilterText = () =>
  createSelector(selectModuleState(), (mState) => mState.filterText)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectItems = () =>
  createSelector(selectModuleState(), (mState) => mState.items)

export const selectOrderBy = () =>
  createSelector(selectModuleState(), (mstate) => mstate.orderBy)

export const selectSortOrder = () =>
  createSelector(selectModuleState(), (mState) => mState.sortOrder)

export const selectTotalItems = () =>
  createSelector(selectModuleState(), (mState) => mState.totalItems)
